import { PERMISSIONS } from './permissions.generated';

export const ADVANCED_GROUP = 'advanced';

/** Component-owned grouping of permission bases; anything unlisted goes to `advanced`. */
export const GROUPS: Record<string, string[]> = {
	people: [
		'User',
		'Group',
		'UserRole',
		'Role',
		'JobTitle',
		'JobLevel',
		'SalaryScale',
		'JobPosition',
		'Career',
		'Expertise',
		'Label',
		'UserDocumentV2',
		'UserDownload',
		'UserExport',
	],
	goals: ['Goal', 'GoalDriver', 'GoalTemplate', 'JobGoalTemplate'],
	competencies: ['Competency', 'CompetencyBookmark', 'CompetencyLevelSet', 'JobCompetency'],
	feedback: [
		'FeedbackQuestion',
		'FeedbackQuestionCategory',
		'FeedbackRequest',
		'FeedbackResponse',
		'FeedbackTemplate',
		'SharedFeedback',
		'Compliment',
	],
	conversations: [
		'CycleInstance',
		'CycleTemplate',
		'MomentInstance',
		'EvaluationForm',
		'EvaluationSubmission',
		'Meeting',
		'Note',
		'NoteTemplate',
		'NoteTemplateCategory',
		'Checkpoint',
		'Task',
		'Comment',
		'TalentReview',
		'TalentReviewSnapshot',
		'TalentReviewTemplate',
	],
	engagement: [
		'EngagementCategory',
		'EngagementQuestion',
		'EngagementReply',
		'EngagementRequest',
		'EngagementRequestListing',
		'EngagementResponse',
		'EngagementSettings',
		'EngagementSubcategory',
		'EngagementTemplate',
		'EngagementTemplateListing',
		'EngagementType',
	],
	learning: ['LearningCourse', 'LearningEvent', 'LearningSubject', 'LearningType', 'GoodHabitz'],
	analytics: ['Analytics', 'AnalyticsV3'],
	ai: ['AIChat', 'AgentChat', 'AINoteTemplateTalkingPoints', 'PromptInstruction'],
	integrations: [
		'Connector',
		'ConnectorLog',
		'ConnectorTask',
		'ConnectorUpload',
		'Provisioning',
		'IdentityProvider',
		'ServiceProvider',
		'WorkspaceSSOSettings',
		'FailedSsoAttempt',
		'SlackConfig',
		'MicrosoftConfig',
		'McpRemote',
		'HubSpot',
		'Styr',
	],
	workspace: [
		'Workspace',
		'WorkspaceFeature',
		'WorkspaceMfaSettings',
		'WorkspacePlan',
		'WorkspaceTheme',
		'Billing',
		'CustomEmailDomain',
		'SentEmail',
		'NotificationTemplate',
		'Announcement',
	],
};

export type PermissionNode = {
	key: string;
	label: string;
	/** Every `Type.capability` string beneath this node (itself, for a leaf). */
	leaves: string[];
	children?: PermissionNode[];
};

export function buildTree(catalog: readonly string[] = PERMISSIONS): PermissionNode[] {
	const bases = new Map<string, string[]>();
	for (const permission of catalog) {
		const base = permission.split('.')[0];
		bases.set(base, [...(bases.get(base) ?? []), permission]);
	}

	const groupOf = (base: string) =>
		Object.keys(GROUPS).find((group) => GROUPS[group].includes(base)) ?? ADVANCED_GROUP;

	const grouped = new Map<string, PermissionNode[]>();
	for (const [base, leaves] of bases) {
		const node: PermissionNode = {
			key: base,
			label: base,
			leaves,
			children: leaves.map((leaf) => ({ key: leaf, label: leaf.split('.')[1], leaves: [leaf] })),
		};
		grouped.set(groupOf(base), [...(grouped.get(groupOf(base)) ?? []), node]);
	}

	return [...Object.keys(GROUPS), ADVANCED_GROUP]
		.filter((group) => grouped.has(group))
		.map((group) => {
			const children = grouped.get(group)!;
			return { key: group, label: group, children, leaves: children.flatMap((c) => c.leaves) };
		});
}

/**
 * How many of the node's leaves are currently whitelisted.
 * @param node - Tree node
 * @param permissions - Current whitelist
 * @returns Number of whitelisted leaves beneath the node
 */
export function selectedCount(node: PermissionNode, permissions: readonly string[]): number {
	return node.leaves.filter((leaf) => permissions.includes(leaf)).length;
}

/**
 * Next whitelist after clicking a node: a fully selected node drops all of its
 * leaves, anything else (none or some) selects all of them.
 * @param node - Clicked tree node
 * @param permissions - Current whitelist
 * @returns The next whitelist
 */
export function toggleNode(node: PermissionNode, permissions: readonly string[]): string[] {
	const complete = selectedCount(node, permissions) === node.leaves.length;
	return complete
		? permissions.filter((permission) => !node.leaves.includes(permission))
		: [...new Set([...permissions, ...node.leaves])];
}
